/**
 * Cart Sync Hook
 *
 * Syncs cart state to Supabase for pre-checkout cart recovery (Tier 1 & 2).
 * - Generates and stores cart_token in localStorage
 * - Debounces sync to avoid excessive API calls
 * - Does NOT require email to save cart state
 */

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CartItem } from '@/types/templates';
import { getSessionId } from '@/lib/analytics/sessionManager';

const CART_TOKEN_KEY = 'mcs_cart_token';
const IDENTITY_EMAIL_KEY = 'mcs_identity_email';
const SYNC_DEBOUNCE_MS = 3000; // 3 seconds

/**
 * Generate a 32-character hex token for cart identification
 */
const generateCartToken = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Get or create cart token from localStorage
 */
export const getCartToken = (): string => {
  if (typeof localStorage === 'undefined') {
    return generateCartToken();
  }

  let token = localStorage.getItem(CART_TOKEN_KEY);
  if (!token) {
    token = generateCartToken();
    localStorage.setItem(CART_TOKEN_KEY, token);
  }
  return token;
};

/**
 * Get stored identity email (if user provided one)
 */
export const getIdentityEmail = (): string | null => {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(IDENTITY_EMAIL_KEY);
};

/**
 * Set identity email for cart recovery
 */
export const setIdentityEmail = (email: string): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(IDENTITY_EMAIL_KEY, email);
};

/**
 * Clear identity email
 */
export const clearIdentityEmail = (): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(IDENTITY_EMAIL_KEY);
};

/**
 * Sync cart to Supabase via RPC
 */
const syncCartToSupabase = async (
  items: CartItem[],
  promoCode: string | null = null,
  identityEmail: string | null = null
): Promise<void> => {
  if (items.length === 0) {
    // Don't sync empty carts
    return;
  }

  try {
    const cartToken = getCartToken();
    const sessionId = getSessionId();

    // Extract product data
    const productIds = items.map((item) => item.template.id);
    const productNames = items.map((item) => item.template.name);
    const cartTotal = items.reduce((sum, item) => sum + item.template.price * item.quantity, 0);

    // Build cart_items JSON array
    const cartItems = items.map((item) => ({
      id: item.template.id,
      name: item.template.name,
      price: item.template.price,
      slug: item.template.slug || null,
      image_url: item.template.image_url || null,
      quantity: item.quantity,
    }));

    // Call the upsert RPC function
    const { error } = await supabase.rpc('upsert_cart_by_token', {
      p_cart_token: cartToken,
      p_product_ids: productIds,
      p_product_names: productNames,
      p_cart_total: cartTotal,
      p_cart_items: cartItems,
      p_session_id: sessionId,
      p_promo_code: promoCode,
      p_identity_email: identityEmail || getIdentityEmail(),
    });

    if (error) {
      console.debug('Cart sync error:', error);
    }
  } catch (err) {
    // Silently fail - cart sync should not break the app
    console.debug('Cart sync failed:', err);
  }
};

/**
 * Hook to sync cart changes to Supabase
 */
export const useCartSync = (
  items: CartItem[],
  promoCode: string | null = null
): void => {
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncedRef = useRef<string>('');

  const debouncedSync = useCallback(() => {
    // Create a hash of the current cart state
    const cartHash = JSON.stringify(items.map((i) => i.template.id).sort());

    // Skip if nothing changed
    if (cartHash === lastSyncedRef.current) {
      return;
    }

    // Clear any pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync
    syncTimeoutRef.current = setTimeout(() => {
      syncCartToSupabase(items, promoCode);
      lastSyncedRef.current = cartHash;
    }, SYNC_DEBOUNCE_MS);
  }, [items, promoCode]);

  useEffect(() => {
    debouncedSync();

    // Cleanup on unmount
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [debouncedSync]);
};

/**
 * Restore cart from Supabase by token
 */
export const restoreCartByToken = async (
  token: string
): Promise<{
  success: boolean;
  items?: Array<{
    id: string;
    name: string;
    price: number;
    slug?: string;
    image_url?: string;
    quantity?: number;
  }>;
  promoCode?: string | null;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('get_cart_by_token', {
      p_token: token,
    });

    if (error) {
      console.error('Cart restore error:', error);
      return { success: false, error: 'Failed to restore cart' };
    }

    if (!data || data.length === 0) {
      return { success: false, error: 'Cart not found or expired' };
    }

    const cart = data[0];

    // Extract items from cart_items or product_ids
    type CartItem = {
      id: string;
      name: string;
      price: number;
      slug?: string;
      image_url?: string;
      quantity?: number;
    };
    
    let items: CartItem[] = [];

    if (cart.cart_items && Array.isArray(cart.cart_items)) {
      // Safely cast JSONB array to our expected type
      items = (cart.cart_items as unknown as CartItem[]).filter(
        (item): item is CartItem => 
          typeof item === 'object' && 
          item !== null && 
          'id' in item && 
          'name' in item && 
          'price' in item
      );
    } else if (cart.product_ids && cart.product_ids.length > 0) {
      // Fallback: use product_ids and names to build items
      // Need to fetch full product data
      const { data: products } = await supabase
        .from('products')
        .select('id, name, slug, price, image_url')
        .in('id', cart.product_ids)
        .eq('is_active', true);

      if (products) {
        items = products;
      }
    }

    return {
      success: true,
      items,
      promoCode: cart.promo_code,
    };
  } catch (err) {
    console.error('Cart restore failed:', err);
    return { success: false, error: 'Failed to restore cart' };
  }
};

/**
 * Mark cart as restored (after successful restoration)
 */
export const markCartRestored = async (token: string): Promise<void> => {
  try {
    await supabase.rpc('mark_cart_restored', { p_token: token });
  } catch (err) {
    console.debug('Failed to mark cart restored:', err);
  }
};

/**
 * Sync identity email to existing cart
 */
export const syncIdentityEmail = async (email: string): Promise<void> => {
  setIdentityEmail(email);

  // Re-sync cart with the new email
  // This requires getting current cart state, which we'll handle through context
};

export default {
  useCartSync,
  getCartToken,
  getIdentityEmail,
  setIdentityEmail,
  clearIdentityEmail,
  restoreCartByToken,
  markCartRestored,
  syncIdentityEmail,
};
